import * as React from "react";
import { clsx } from "clsx";

interface Star {
  id: number;
  left: number;
  top: number;
  animationDelay: number;
  animationDuration: number;
}
export interface IgniteStarryBackgroundProps
  extends React.HTMLAttributes<HTMLDivElement> {
  showTitle?: boolean;
  title?: string;
  subtitle?: string;
  data?: [Star[], Star[], Star[]];
}

// Generate random star positions for different layers
export const generateStars = (count: number, _size: number) => {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    animationDelay: Math.random() * 10, // Longer delays for variety
    animationDuration: 80 + Math.random() * 120, // 80-200s - much slower
  }));
};

const IgniteStarryBackground: React.FC<
  IgniteStarryBackgroundProps & React.RefAttributes<HTMLDivElement>
> = ({
  className,
  showTitle = true,
  title = "Ignite 2025",
  subtitle = "Where Innovation Meets Excellence",
  data = [[], [], []],
  ref,
  ...props
}) => {
  const [smallStars, mediumStars, largeStars] = data;
  return (
    <div
      ref={ref}
      className={clsx("relative w-full h-full overflow-hidden", className)}
      style={{
        background: `
          radial-gradient(ellipse at bottom,
            #1a0d3d 0%,
            #12094a 20%,
            #2a1055 40%,
            #3d1542 60%,
            #4a1630 80%,
            #050208 100%
          )
        `,
      }}
      {...props}
    >
      {/* Small Stars Layer */}
      <div className="absolute inset-0">
        {smallStars.map((star) => (
          <div
            key={`small-${star.id}`}
            className="absolute rounded-full bg-white"
            style={{
              width: "1px",
              height: "1px",
              left: `${star.left}%`,
              top: `${star.top}%`,
              boxShadow: `0 0 3px #FDCA41, 0 0 6px rgba(253, 202, 65, 0.5)`,
              animation: `float-up ${star.animationDuration}s linear infinite`,
              animationDelay: `${star.animationDelay}s`,
            }}
          />
        ))}
      </div>

      {/* Medium Stars Layer */}
      <div className="absolute inset-0">
        {mediumStars.map((star) => (
          <div
            key={`medium-${star.id}`}
            className="absolute rounded-full"
            style={{
              width: "2px",
              height: "2px",
              left: `${star.left}%`,
              top: `${star.top}%`,
              background: `#FDCA41`,
              boxShadow: `0 0 6px #FDCA41, 0 0 12px rgba(253, 202, 65, 0.6)`,
              animation: `float-up ${star.animationDuration}s linear infinite`,
              animationDelay: `${star.animationDelay}s`,
            }}
          />
        ))}
      </div>

      {/* Large Stars Layer */}
      <div className="absolute inset-0">
        {largeStars.map((star) => (
          <div
            key={`large-${star.id}`}
            className="absolute rounded-full"
            style={{
              width: "3px",
              height: "3px",
              left: `${star.left}%`,
              top: `${star.top}%`,
              background: `#E32F2D`,
              boxShadow: `
                0 0 8px #E32F2D,
                0 0 16px rgba(227, 47, 45, 0.6),
                0 0 24px rgba(227, 47, 45, 0.4)
              `,
              animation: `float-up ${star.animationDuration}s linear infinite`,
              animationDelay: `${star.animationDelay}s`,
            }}
          />
        ))}
      </div>

      {/* Shooting Stars */}
      <div className="absolute inset-0">
        {[...Array(3)].map((_, i) => (
          <div
            key={`shooting-${i}`}
            className="absolute w-1 h-1 rounded-full opacity-0"
            style={{
              background: `linear-gradient(45deg, #FDCA41, transparent)`,
              left: `${20 + i * 30}%`,
              top: `${10 + i * 20}%`,
              animation: `shooting-star ${8 + i * 2}s ease-out infinite`,
              animationDelay: `${i * 3}s`,
            }}
          />
        ))}
      </div>

      {/* Title Overlay */}
      {showTitle && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="text-center px-4">
            <h1
              className="text-4xl md:text-6xl lg:text-7xl font-light tracking-[0.2em] mb-4"
              style={{
                background: `linear-gradient(135deg,
                  #FDCA41 0%,
                  white 30%,
                  #E32F2D 60%,
                  #AC2B53 100%
                )`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                filter: "drop-shadow(0 0 20px rgba(253, 202, 65, 0.3))",
              }}
            >
              {title}
            </h1>
            {subtitle && (
              <p
                className="text-lg md:text-xl text-white/90 font-light tracking-wider"
                style={{
                  textShadow: "0 0 10px rgba(227, 47, 45, 0.5)",
                }}
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>
      )}

      <style
        dangerouslySetInnerHTML={{
          __html: `
          @keyframes float-up {
            0% {
              transform: translateY(100vh) rotate(0deg);
              opacity: 0;
            }
            10% {
              opacity: 1;
            }
            90% {
              opacity: 1;
            }
            100% {
              transform: translateY(-100vh) rotate(360deg);
              opacity: 0;
            }
          }

          @keyframes shooting-star {
            0% {
              opacity: 0;
              transform: translateX(0) translateY(0) scale(0);
            }
            5% {
              opacity: 1;
              transform: translateX(0) translateY(0) scale(1);
            }
            15% {
              opacity: 1;
              transform: translateX(300px) translateY(150px) scale(1);
            }
            20% {
              opacity: 0;
              transform: translateX(400px) translateY(200px) scale(0);
            }
            100% {
              opacity: 0;
              transform: translateX(400px) translateY(200px) scale(0);
            }
          }

          @keyframes twinkle {
            0%, 100% { opacity: 0.8; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.2); }
          }
        `,
        }}
      />
    </div>
  );
};

IgniteStarryBackground.displayName = "IgniteStarryBackground";

export { IgniteStarryBackground };
